import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { useToast } from '@/hooks/use-toast';
import { Snowflake, Leaf, Flower2, Heart, PartyPopper, Sun, Palette, Eye, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Json } from '@/integrations/supabase/types';

interface Decorations {
  type: string;
  intensity: string;
  colors: string[];
}

interface Theme {
  id: string;
  name: string;
  slug: string;
  is_active: boolean;
  icon: string | null;
  decorations: Decorations | null;
  accent_colors: Json | null;
  banner: Json | null;
  created_at: string;
  updated_at: string;
}

const themeIcons: Record<string, React.ReactNode> = {
  '❄️': <Snowflake className="w-6 h-6 text-blue-400" />,
  '🌸': <Flower2 className="w-6 h-6 text-pink-400" />,
  '☀️': <Sun className="w-6 h-6 text-yellow-500" />,
  '🍂': <Leaf className="w-6 h-6 text-orange-500" />,
  '❤️': <Heart className="w-6 h-6 text-red-500" />,
  '🎉': <PartyPopper className="w-6 h-6 text-purple-500" />,
  '🚫': <span className="text-2xl">🚫</span>,
};

const intensityLabels: Record<string, string> = {
  subtle: 'Dezent',
  medium: 'Mittel',
  strong: 'Intensiv',
};

export function ThemesManagement() {
  const [themes, setThemes] = useState<Theme[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);
  const [previewTheme, setPreviewTheme] = useState<Theme | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    loadThemes();
  }, []);

  const loadThemes = async () => {
    try {
      const { data, error } = await supabase
        .from('site_themes')
        .select('*')
        .order('name');

      if (error) throw error;
      
      // Transform the data to match our Theme interface
      const transformedData: Theme[] = (data || []).map(item => ({
        ...item,
        decorations: item.decorations as unknown as Decorations | null,
      }));
      
      setThemes(transformedData);
    } catch (error: any) {
      toast({
        title: 'Fehler beim Laden',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const activateTheme = async (themeId: string) => {
    setUpdating(themeId);
    try {
      // Deactivate all themes first
      await supabase
        .from('site_themes')
        .update({ is_active: false })
        .neq('id', '00000000-0000-0000-0000-000000000000');

      // Activate selected theme
      const { error } = await supabase
        .from('site_themes')
        .update({ is_active: true })
        .eq('id', themeId);

      if (error) throw error;

      setThemes(themes.map(t => ({
        ...t,
        is_active: t.id === themeId,
      })));

      const theme = themes.find(t => t.id === themeId);
      toast({
        title: 'Theme aktiviert',
        description: `"${theme?.name}" ist jetzt aktiv auf der Hauptseite.`,
      });
    } catch (error: any) {
      toast({
        title: 'Fehler',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setUpdating(null);
    }
  };

  const deactivateThemes = async () => {
    setUpdating('deactivate');
    try {
      const { error } = await supabase
        .from('site_themes')
        .update({ is_active: false })
        .neq('id', '00000000-0000-0000-0000-000000000000');

      if (error) throw error;

      setThemes(themes.map(t => ({ ...t, is_active: false })));
      toast({
        title: 'Themes deaktiviert',
        description: 'Auf der Hauptseite werden jetzt keine Dekorationen angezeigt.',
      });
    } catch (error: any) {
      toast({
        title: 'Fehler',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setUpdating(null);
    }
  };

  const updateIntensity = async (themeId: string, intensity: string) => {
    try {
      const theme = themes.find(t => t.id === themeId);
      if (!theme || !theme.decorations) return;

      const newDecorations = {
        ...theme.decorations,
        intensity,
      };

      const { error } = await supabase
        .from('site_themes')
        .update({ decorations: newDecorations as unknown as Json })
        .eq('id', themeId);

      if (error) throw error;

      setThemes(themes.map(t =>
        t.id === themeId
          ? { ...t, decorations: newDecorations }
          : t
      ));

      toast({
        title: 'Intensität angepasst',
        description: `Intensität auf "${intensityLabels[intensity]}" gesetzt.`,
      });
    } catch (error: any) {
      toast({
        title: 'Fehler',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const activeTheme = themes.find(t => t.is_active);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Active Theme Card */}
      <Card className="border-2 border-cyan-200 bg-gradient-to-br from-cyan-50 to-blue-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="w-5 h-5 text-cyan-600" />
            Aktives Saisonales Theme
          </CardTitle>
          <CardDescription>
            Das aktuell aktive Theme wird auf der Hauptseite angezeigt.
          </CardDescription>
        </CardHeader>
        <CardContent>
          	{activeTheme ? (
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-4">
              <div className="flex items-center gap-4 flex-1">
                <div className="p-3 bg-white rounded-xl shadow-sm">
                  {themeIcons[activeTheme.icon || '🚫'] || <span className="text-2xl">{activeTheme.icon}</span>}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg">{activeTheme.name}</h3>
                  <p className="text-sm text-slate-600">
                    {activeTheme.decorations?.type === 'none'
                      ? 'Keine Dekorationen'
                      : `Dekoration: ${activeTheme.decorations?.type} • Intensität: ${intensityLabels[activeTheme.decorations?.intensity || 'medium']}`
                    }
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between gap-3 sm:justify-end">
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-500" />
                  <span className="text-sm font-medium text-green-600">Aktiv</span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={deactivateThemes}
                  disabled={updating === 'deactivate'}
                >
                  {updating === 'deactivate' ? 'Deaktivieren…' : 'Deaktivieren'}
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-slate-600">Kein Theme aktiv. Wähle ein Theme aus der Liste unten.</p>
          )}
        </CardContent>
      </Card>

      {/* Available Themes */}
      <Card>
        <CardHeader>
          <CardTitle>Verfügbare Themes</CardTitle>
          <CardDescription>
            Klicke auf "Aktivieren" um ein Theme auf der Hauptseite anzuzeigen.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {themes.map((theme) => (
              <div
                key={theme.id}
                className={`relative p-4 rounded-xl border-2 transition-all ${
                  theme.is_active
                    ? 'border-cyan-400 bg-cyan-50 shadow-md'
                    : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${theme.is_active ? 'bg-cyan-100' : 'bg-slate-100'}`}>
                      {themeIcons[theme.icon || '🚫'] || <span className="text-xl">{theme.icon}</span>}
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">{theme.name}</h4>
                      <p className="text-xs text-slate-500 capitalize">
                        {theme.decorations?.type === 'none' ? 'Keine Deko' : theme.decorations?.type}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Intensity Slider (only for themes with decorations) */}
                {theme.decorations?.type !== 'none' && (
                  <div className="mb-4">
                    <label className="text-xs text-slate-500 mb-2 block">
                      Intensität: {intensityLabels[theme.decorations?.intensity || 'medium']}
                    </label>
                    <div className="flex gap-2">
                      {['subtle', 'medium', 'strong'].map((intensity) => (
                        <button
                          key={intensity}
                          onClick={() => updateIntensity(theme.id, intensity)}
                          className={`flex-1 py-1 px-2 text-xs rounded-md transition-all ${
                            theme.decorations?.intensity === intensity
                              ? 'bg-cyan-600 text-white'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {intensityLabels[intensity]}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-2">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => setPreviewTheme(theme)}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Vorschau
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle>Vorschau: {theme.name}</DialogTitle>
                      </DialogHeader>
                      <div className="relative h-80 bg-gradient-to-br from-slate-50 to-blue-50 rounded-lg overflow-hidden">
                        <ThemePreview theme={theme} />
                        <div className="absolute bottom-4 left-4 right-4 bg-white/80 backdrop-blur-sm rounded-lg p-4">
                          <p className="text-sm text-slate-600">
                            So wird die Dekoration auf deiner Hauptseite aussehen.
                          </p>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>

                  <Button
                    size="sm"
                    className={`flex-1 ${theme.is_active ? 'bg-green-600 hover:bg-green-700' : ''}`}
                    onClick={() => activateTheme(theme.id)}
                    disabled={updating === theme.id || theme.is_active}
                  >
                    {updating === theme.id ? (
                      <span className="animate-spin">⏳</span>
                    ) : theme.is_active ? (
                      <>
                        <Check className="w-4 h-4 mr-1" />
                        Aktiv
                      </>
                    ) : (
                      'Aktivieren'
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Preview Component for the Dialog
function ThemePreview({ theme }: { theme: Theme }) {
  const decorationType = theme.decorations?.type;
  const intensity = theme.decorations?.intensity || 'medium';
  const count = intensity === 'subtle' ? 10 : intensity === 'medium' ? 20 : 35;

  if (decorationType === 'none' || !decorationType) {
    return (
      <div className="flex items-center justify-center h-full text-slate-400">
        Keine Dekorationen
      </div>
    );
  }

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {Array.from({ length: count }).map((_, i) => (
        <span
          key={i}
          className="absolute animate-fall text-2xl"
          style={{
            left: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 5}s`,
            animationDuration: `${5 + Math.random() * 5}s`,
          }}
        >
          {decorationType === 'snowflakes' && '❄️'}
          {decorationType === 'leaves' && ['🍂', '🍁', '🍃'][Math.floor(Math.random() * 3)]}
          {decorationType === 'petals' && '🌸'}
          {decorationType === 'hearts' && '❤️'}
          {decorationType === 'confetti' && ['🎊', '🎉', '✨'][Math.floor(Math.random() * 3)]}
          {decorationType === 'sunshine' && ['☀️', '✨'][Math.floor(Math.random() * 2)]}
        </span>
      ))}
    </div>
  );
}

export default ThemesManagement;
